import React from 'react'
import styled from 'styled-components'
import {usePanelsTemplate} from 'Event/template/Panels'
import {useAttendeeVariables} from 'Event'
import {useHasTechCheck} from 'Event/EventProvider'
import {Icon} from 'lib/fontawesome/Icon'
import Configurable from 'organization/Event/Configurable'
import StepConfig from 'Event/template/Panels/check-in/StepConfig'
import {useHasWaiver} from 'Event/Step2/Waivers'
import {useRequiresPassword} from 'Event/Step1/SetPasswordForm'

export type Step = 1 | 2 | 3

export default function StepIndicator(props: {
  step: Step
  className?: string
  horizontal?: boolean
}) {
  const template = usePanelsTemplate()
  const hasTechCheck = useHasTechCheck()
  const requiresPassword = useRequiresPassword()

  const hasWaiver = useHasWaiver()

  return (
    <Box className={props.className}>
      <LabelContainer>
        <CheckInLabel />
        <StepLabel step={props.step} />
      </LabelContainer>
      <IconContainer horizontal={props.horizontal}>
        <StepBox
          step={1}
          currentStep={props.step}
          horizontal={props.horizontal}
          icon={template.step1Icon}
          showing={requiresPassword}
          isLast={!hasWaiver && !hasTechCheck}
        />
        <StepBox
          step={2}
          currentStep={props.step}
          horizontal={props.horizontal}
          icon={template.step2Icon}
          showing={hasWaiver}
          isLast={!hasTechCheck}
        />
        <StepBox
          step={3}
          currentStep={props.step}
          horizontal={props.horizontal}
          icon={template.step3Icon}
          showing={hasTechCheck}
          isLast
        />
      </IconContainer>
    </Box>
  )
}

function CheckInLabel() {
  const template = usePanelsTemplate()

  const v = useAttendeeVariables()
  return <Label aria-label="check in label">{v(template.checkInTitle)}</Label>
}

function StepLabel(props: {step: Step}) {
  const template = usePanelsTemplate()

  const labels = {
    1: template.step1Label,
    2: template.step2Label,
    3: template.step3Label,
  }

  const label = labels[props.step]

  const v = useAttendeeVariables()
  return <Label color={template.stepLabelColor}>{v(label)}</Label>
}

function StepIcon(props: {
  icon: string
  isActive: boolean
  'aria-label'?: string
}) {
  const {isActive} = props
  const template = usePanelsTemplate()
  const color = isActive ? template.stepIconColor : template.stepInactiveColor

  return (
    <StyledIcon
      iconClass={props.icon}
      color={color}
      aria-label={props['aria-label']}
    />
  )
}

function Divider(props: {
  isActive: boolean
  horizontal?: boolean
  showing: boolean
}) {
  const {isActive, showing, horizontal} = props
  const template = usePanelsTemplate()
  const color = isActive ? template.stepIconColor : template.stepInactiveColor

  if (!showing) {
    return null
  }

  return <DividerLine color={color} horizontal={horizontal} />
}

function StepBox(props: {
  step: 1 | 2 | 3
  currentStep: number
  horizontal?: boolean
  icon: string
  showing: boolean
  isLast?: boolean
}) {
  const {showing, isLast, currentStep, step, icon, horizontal} = props

  if (!showing) {
    return null
  }

  const isIconActive = currentStep >= step
  const isDividerActive = currentStep > step

  return (
    <>
      <Configurable>
        <StepConfig step={step} />
        <StepIcon
          icon={icon}
          isActive={isIconActive}
          aria-label={`step ${step} icon`}
        />
      </Configurable>
      <Divider
        horizontal={horizontal}
        isActive={isDividerActive}
        showing={!isLast}
      />
    </>
  )
}

const Label = styled.h4<{
  color?: string
}>`
  ${(props) => (props.color ? `color: ${props.color};` : '')}
  font-size: 24px;
  font-weight: bold;
  margin: 0;
  line-height: 36px;
`

const Box = styled.div`
  text-align: center;
`

const LabelContainer = styled.div`
  margin-bottom: 40px;
`

const IconContainer = styled.div<{
  horizontal?: boolean
}>`
  display: flex;
  flex-direction: ${(props) => (props.horizontal ? 'row' : 'column')};
  align-items: center;
  justify-content: center;
`

const StyledIcon = styled(Icon)`
  font-size: 40px;
`

const DividerLine = styled.div<{horizontal?: boolean; color: string}>`
  background: ${(props) => props.color};
  width: ${(props) => (props.horizontal ? '120px' : '4px')};
  height: ${(props) => (props.horizontal ? '4px' : '80px')};
  margin: 8px;
  border-radius: 10px;
`
